declare global {
  interface Window {
    OTK_FEEDBACK_API_BASE_URL?: string
    OTKFeedback?: {
      init: (options: { apiBaseUrl: string; bottomOffsetPx?: number }) => void
      show: (options: { userId: string; entityId: string; key: string; meta?: Record<string, any>; delayMs?: number }) => void
      maybeShowFromUrl: (options: {
        userId: string
        entityId: string
        key: string
        meta?: Record<string, any>
        delayMs?: number
        queryFlag?: string
        queryKey?: string
        sessionPrefix?: string
        clearQuery?: boolean
      }) => Promise<void>
    }
  }
}

type EnsureFeedbackWidgetLoadedOptions = {
  scriptSrc?: string
  apiBaseUrl?: string
  waitForGlobalMs?: number
}

const SCRIPT_ID = 'otk-feedback-widget'

function getOtkFeedbackApi() {
  return window.OTKFeedback
}

function waitForOtkFeedbackApi(timeoutMs: number) {
  const startedAt = Date.now()
  return new Promise<void>((resolve, reject) => {
    const tick = () => {
      if (getOtkFeedbackApi()) return resolve()
      if (Date.now() - startedAt >= timeoutMs) return reject(new Error('Feedback widget global function did not appear in time'))
      setTimeout(tick, 50)
    }
    tick()
  })
}

function setApiBaseUrl(apiBaseUrl?: string) {
  if (!apiBaseUrl) return
  if (typeof window === 'undefined') return
  if (window.OTK_FEEDBACK_API_BASE_URL === apiBaseUrl) return
  window.OTK_FEEDBACK_API_BASE_URL = apiBaseUrl
}

export function ensureFeedbackWidgetLoaded(options: EnsureFeedbackWidgetLoadedOptions = {}) {
  const {
    scriptSrc = process.env.REACT_APP_FEEDBACK_WIDGET_SRC,
    apiBaseUrl = process.env.REACT_APP_FEEDBACK_API_BASE_URL,
    waitForGlobalMs = 2000,
  } = options

  setApiBaseUrl(apiBaseUrl)
  const resolvedApiBaseUrl = apiBaseUrl ?? (typeof window !== 'undefined' ? window.OTK_FEEDBACK_API_BASE_URL ?? '' : '')

  if (typeof window === 'undefined') return Promise.resolve()
  if (getOtkFeedbackApi()) return Promise.resolve()

  if (!scriptSrc) return Promise.reject(new Error('Feedback widget script src is not configured'))

  const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
  if (existing) {
    return new Promise<void>((resolve, reject) => {
      if (getOtkFeedbackApi()) return resolve()
      existing.addEventListener(
        'load',
        () => {
          waitForOtkFeedbackApi(waitForGlobalMs)
            .then(() => {
              if (window.OTKFeedback) {
                window.OTKFeedback.init({ apiBaseUrl: resolvedApiBaseUrl, bottomOffsetPx: 100 })
              }
              resolve()
            })
            .catch(reject)
        },
        { once: true },
      )
      existing.addEventListener('error', () => reject(new Error('Failed to load feedback widget script')), { once: true })
    })
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = scriptSrc
    script.async = true
    script.defer = true
    script.addEventListener(
      'load',
      () => {
        waitForOtkFeedbackApi(waitForGlobalMs)
          .then(() => {
            if (window.OTKFeedback) {
              window.OTKFeedback.init({ apiBaseUrl: resolvedApiBaseUrl, bottomOffsetPx: 100 })
            }
            resolve()
          })
          .catch(reject)
      },
      { once: true },
    )
    script.addEventListener('error', () => reject(new Error('Failed to load feedback widget script')), { once: true })
    document.head.appendChild(script)
  })
}

export async function showFeedbackWidget(params: { userId: string; entityId: string; key: string; meta?: Record<string, any> }) {
  await ensureFeedbackWidgetLoaded()
  const api = getOtkFeedbackApi()
  if (!api) throw new Error('OTKFeedback API is not available after loading script')

  await api.maybeShowFromUrl({
    userId: params.userId,
    entityId: params.entityId,
    key: params.key,
    meta: params.meta,
  })
}
